import * as React from "react"

import { cva } from "@iyk/ui"

export function AnimatedCanvasMarquee({
  children,
  className,
  speed,
  space = 1,
  ...props
}: AnimatedCanvasMarqueeProps) {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null)

  React.useEffect(() => {
    if (canvasRef.current) {
      return animateCanvasMarquee(canvasRef.current, {
        content: String(children ?? "").trim(),
        speed: speed,
        space: space,
      })
    }
  }, [])

  return (
    <canvas ref={canvasRef} className={classForAnimationCanvasMarquee({ className })} {...props} />
  )
}

const classForAnimationCanvasMarquee = cva(["w-full"])

type AnimatedCanvasMarqueeProps = Omit<React.ComponentProps<"canvas">, "children"> & {
  className?: string
  children: string
  speed: number
  space?: number
}

const animateCanvasMarquee = (
  canvas: HTMLCanvasElement,
  { content, speed, space }: AnimateCanvasMarqueeProps,
) => {
  const computedStyle = getComputedStyle(canvas)
  const ctx = canvas.getContext("2d")!

  const internals = {
    frameId: 0,
    sizeY: 0,
    sizeX: 0,
    observer: new ResizeObserver(() => setSize()),
  } as {
    frameId: number
    sizeX: number
    sizeY: number
    observer: ResizeObserver
    metrics: TextMetrics
  }

  const setSize = () => {
    internals.sizeX = canvas.getBoundingClientRect().width
    internals.sizeY = parseFloat(computedStyle.fontSize)
    internals.metrics = ctx.measureText(content)

    canvas.width = internals.sizeX * 2
    canvas.height = internals.sizeY * 2

    ctx.font = `300 ${internals.sizeY * 2}px "Regola Pro",sans-serif`
    ctx.fillStyle = "var(--foreground)"
    ctx.textBaseline = "middle"
  }

  internals.observer.observe(canvas)

  const draw = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = computedStyle.color

    x += speed

    if (x <= -internals.metrics.width || x >= internals.metrics.width) {
      x = 0
    }

    let startX = x - internals.metrics.width

    while (startX < canvas.width) {
      ctx.fillText(content, startX, internals.sizeY)

      startX += internals.metrics.width
    }

    internals.frameId = requestAnimationFrame(draw)
  }

  content += " ".repeat(space)

  let x = 0

  setSize()
  draw()

  return () => {
    internals.observer.disconnect()

    cancelAnimationFrame(internals.frameId)
  }
}

type AnimateCanvasMarqueeProps = {
  content: string
  speed: number
  space: number
}
